import React, {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useToast} from "../../modules/layout/components/ToastContext";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import Loader from "../../modules/layout/components/Loader";
import {Field, Form, Formik} from "formik";
import * as Yup from "yup";
import FiltersCard from "../../modules/layout/components/FiltersCard";
import {FormikDatePicker} from "../../modules/layout/components/FormikDatePicker";
import {endOfDay, startOfDay, toApiDateTime} from "../../helpers/DateTimeHelper";
import {getAll} from "../_request";
import {
    IFilterStatisticsModel,
    IGatewayApiResponse,
    IP2GandG2GCount,
    IUserCountSummary
} from "../../interfaces/IUtilities";

// Default date range is the last 7 days, today included: from 00:00 six days ago to 23:59 today.
const getDefaultFilters = (): IFilterStatisticsModel => {
    const to = endOfDay(new Date());
    const from = startOfDay(new Date());
    from.setDate(from.getDate() - 6);
    return {from, to, targetProfileId: '', trxName: '', nrTrx: ''};
};

// Backend returns a .NET TimeSpan-formatted string (e.g. "00:00:04.6658738") when data exists,
// or an arbitrary human-readable (untranslated) message when there's nothing to average.
// Match the actual shape rather than any specific wording, so a backend copy change can't silently
// leak untranslated text into the UI again.
const TIMESPAN_PATTERN = /^\d+:\d{2}:\d{2}(\.\d+)?$/;
const isTimeSpanFormat = (value: string) => TIMESPAN_PATTERN.test(value.trim());

// Rounds the fractional seconds of a .NET TimeSpan string (e.g. "00:00:04.6658738") to
// millisecond precision (3 decimals), carrying into seconds/minutes/hours on round-up.
const roundTimeSpanToMillis = (value: string): string => {
    const trimmed = value.trim();
    const [hms, fraction] = trimmed.split('.');
    const [h, m, s] = hms.split(':').map(Number);
    const seconds = fraction ? Number(`${s}.${fraction}`) : s;

    let totalMs = Math.round((h * 3600 + m * 60 + seconds) * 1000);
    const hoursOut = Math.floor(totalMs / 3600000);
    totalMs -= hoursOut * 3600000;
    const minutesOut = Math.floor(totalMs / 60000);
    totalMs -= minutesOut * 60000;
    const secondsOut = Math.floor(totalMs / 1000);
    const millisOut = totalMs - secondsOut * 1000;

    const pad = (n: number, len = 2) => String(n).padStart(len, '0');
    return `${hoursOut}:${pad(minutesOut)}:${pad(secondsOut)}.${pad(millisOut, 3)}`;
};

interface IQueryOptions {
    targetProfileId?: boolean;
    trxName?: boolean;
    nrTrx?: boolean;
}

const buildQuery = (filters: IFilterStatisticsModel, options: IQueryOptions = {}) => {
    const parts: string[] = [];
    const start = toApiDateTime(filters.from);
    const end = toApiDateTime(filters.to, true);
    if (start) parts.push(`start=${encodeURIComponent(start)}`);
    if (end) parts.push(`end=${encodeURIComponent(end)}`);
    if (options.targetProfileId && filters.targetProfileId) {
        parts.push(`targetProfileId=${encodeURIComponent(filters.targetProfileId)}`);
    }
    if (options.trxName && filters.trxName) {
        parts.push(`trxName=${encodeURIComponent(filters.trxName)}`);
    }
    if (options.nrTrx && filters.nrTrx) {
        parts.push(`nrTrx=${encodeURIComponent(filters.nrTrx)}`);
    }
    return parts.length ? '?' + parts.join('&') : '';
};

const getRespStatus = (res: any): number | undefined => res?.response?.status ?? res?.status;

interface IStatisticsData {
    userCounts: IUserCountSummary | null;
    newUsersRatio: string | null;
    avgTrxTime: string | null;
    p2gAndG2g: IP2GandG2GCount | null;
    nrOfTrxForUser: number | null;
    nrOfLoginsForUser: number | null;
    profileNotFound: boolean;
}

// nrTrx has no real upper bound: both GetNrOfTrxForP2GandG2G and GetAverageTimeOfTrxs accept any
// value, and are left unbounded server-side when left blank. So this field must not impose an
// artificial ceiling of its own.
const filtersValidationSchema = Yup.object({
    nrTrx: Yup.number()
        .transform((v, orig) => (orig === '' ? undefined : v))
        .min(1, 'NrTrxTooLow')
        .integer('NrTrxTooLow'),
    from: Yup.mixed().nullable(),
    to: Yup.mixed().nullable()
}).test('date-range-required', 'StatisticsDateRangeRequired', function (values) {
    if (!values?.from || !values?.to) {
        return this.createError({path: 'dateRange', message: 'StatisticsDateRangeRequired'});
    }
    return true;
});

const emptyData: IStatisticsData = {
    userCounts: null,
    newUsersRatio: null,
    avgTrxTime: null,
    p2gAndG2g: null,
    nrOfTrxForUser: null,
    nrOfLoginsForUser: null,
    profileNotFound: false
};

const Statistics = () => {
    const {t} = useTranslation();
    const {showToast} = useToast();
    const navigate = useNavigate();

    const [activeFilters, setActiveFilters] = useState<IFilterStatisticsModel>(getDefaultFilters);
    const [data, setData] = useState<IStatisticsData>(emptyData);
    const [loading, setLoading] = useState(true);

    const loadAll = useCallback(async (filters: IFilterStatisticsModel) => {
        try {
            setLoading(true);

            const hasProfileId = !!filters.targetProfileId;
            const [userCountsRes, ratioRes, avgTimeRes, p2gG2gRes, nrTrxRes, nrLoginsRes] = await Promise.all([
                getAll('/api/statistics/GetUserCountByProfileType', buildQuery(filters)),
                getAll('/api/statistics/GetNewUsersRatio', buildQuery(filters)),
                getAll('/api/statistics/GetAverageTimeOfTrxs', buildQuery(filters, {trxName: true, nrTrx: true})),
                getAll('/api/statistics/GetNrOfTrxForP2GandG2G', buildQuery(filters, {nrTrx: true})),
                hasProfileId ? getAll('/api/statistics/GetNrOfTrxForUsers', buildQuery(filters, {targetProfileId: true})) : Promise.resolve(null),
                hasProfileId ? getAll('/api/statistics/GetNrOfLoginsForUsers', buildQuery(filters, {targetProfileId: true})) : Promise.resolve(null)
            ]);

            // Only the four always-on aggregate calls represent real system health — a bad status
            // there means something is actually broken (auth, policy, backend down), so the app-wide
            // convention (redirect on 401/403/500) applies.
            const aggregateResponses = [userCountsRes, ratioRes, avgTimeRes, p2gG2gRes];
            const aggregateStatuses = aggregateResponses.map(getRespStatus).filter((s): s is number => s !== undefined);

            // GetNrOfLoginsForUsers is the one profile-lookup call RegisterEnrollment maps to a real
            // 404 when the profile id doesn't resolve (see CrossRegisterEnrollmentService.cs), so its
            // status — not GetNrOfTrxForUsers's, which 500s for the same "not found" case — is the
            // reliable signal for "bad input" vs "something is actually broken" on this section.
            const nrLoginsStatus = hasProfileId ? getRespStatus(nrLoginsRes) : undefined;
            const profileNotFound = hasProfileId && nrLoginsStatus === 404;
            if (profileNotFound) {
                showToast(t('StatisticsProfileNotFound', {profileId: String(filters.targetProfileId)}), 'info');
            }
            const profileLookupFailedForRealReason = hasProfileId && nrLoginsStatus !== undefined
                && nrLoginsStatus !== 200 && !profileNotFound;

            // A 200 on logins proves the profile exists, so a non-200 on trx at that point can't be
            // "not found" either — it's a real failure that would otherwise hide silently behind a "—".
            const nrTrxStatus = hasProfileId ? getRespStatus(nrTrxRes) : undefined;
            const trxFailedForRealReason = nrLoginsStatus === 200
                && nrTrxStatus !== undefined && nrTrxStatus !== 200;

            const statuses = [
                ...aggregateStatuses,
                ...(profileLookupFailedForRealReason ? [nrLoginsStatus] : []),
                ...(trxFailedForRealReason ? [nrTrxStatus] : [])
            ];
            const failedStatus = statuses.find(s => s !== 200);

            if (failedStatus === 403) { navigate('/error/403'); return; }
            if (failedStatus === 401) { navigate('/auth/login'); return; }
            if (failedStatus === 500) { navigate('/error/500'); return; }
            if (failedStatus !== undefined) {
                showToast(t('StatusCode', {code: String(failedStatus)}), 'error');
            }

            const unwrap = <T,>(res: any): T | null => {
                const body = res?.data as IGatewayApiResponse<T> | undefined;
                return body && body.IsSuccess ? body.Data : null;
            };

            setData({
                userCounts: unwrap<IUserCountSummary>(userCountsRes),
                newUsersRatio: unwrap<string>(ratioRes),
                avgTrxTime: unwrap<string>(avgTimeRes),
                p2gAndG2g: unwrap<IP2GandG2GCount>(p2gG2gRes),
                nrOfTrxForUser: hasProfileId && !profileNotFound ? unwrap<number>(nrTrxRes) : null,
                nrOfLoginsForUser: hasProfileId && !profileNotFound ? unwrap<number>(nrLoginsRes) : null,
                profileNotFound
            });
        } catch (error) {
            showToast(t('ConnectionError', {error: String(error)}), 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast, t, navigate]);

    useEffect(() => {
        loadAll(activeFilters);
        // Only run once on mount, against the default filters computed in initial state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadAll]);

    const handleSearch = async (filters: IFilterStatisticsModel) => {
        setActiveFilters(filters);
        await loadAll(filters);
    };

    // GetAverageTimeOfTrxs no longer clamps server-side — it uses nrTrx as given, and is left
    // unbounded when the field is left blank. Mirror that exactly so the displayed note always
    // matches what was actually queried.
    const requestedNrTrx = Number(activeFilters.nrTrx);
    const averageTrxTimeCap = activeFilters.nrTrx && Number.isFinite(requestedNrTrx)
        ? requestedNrTrx
        : null;

    return (
        <div className="gg-statistics container mt-4">
            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('Statistics')}
                ]}/>

            <h3 className="mb-3">{t('Statistics')}</h3>

            <FiltersCard isActive={true}>
                <Formik initialValues={activeFilters} onSubmit={handleSearch}
                        validationSchema={filtersValidationSchema} enableReinitialize>
                    {({resetForm, values, errors, touched, submitCount}) => {
                        const fromVal = values.from ? new Date(values.from) : null;
                        const toVal = values.to ? new Date(values.to) : null;
                        const today = new Date();
                        return (
                            <Form>
                                <div className="row">
                                    <div className="col-md-6 col-lg-3">
                                        <label className="form-label w-100">{t('DateFrom')}
                                            <FormikDatePicker name="from" showTime placeholderText={t('ChooseDate')}
                                                              maxDate={toVal && toVal < today ? toVal : today}/>
                                        </label>
                                    </div>
                                    <div className="col-md-6 col-lg-3">
                                        <label className="form-label w-100">{t('DateTo')}
                                            <FormikDatePicker name="to" showTime endOfDayDefault placeholderText={t('ChooseDate')}
                                                              minDate={fromVal}
                                                              maxDate={today}/>
                                        </label>
                                    </div>
                                    <div className="col-md-6 col-lg-3">
                                        <label className="form-label w-100">{t('TargetProfileId')}
                                            <Field className="form-control" name="targetProfileId"/>
                                        </label>
                                    </div>
                                    <div className="col-md-6 col-lg-3">
                                        <label className="form-label w-100">{t('TrxName')}
                                            <Field className="form-control" name="trxName"/>
                                        </label>
                                    </div>
                                    <div className="col-md-6 col-lg-3">
                                        <label className="form-label w-100">{t('NrTrx')}
                                            <Field type="number" min={1} className="form-control" name="nrTrx"
                                                   placeholder={t('NrTrxPlaceholder')}/>
                                        </label>
                                        {touched.nrTrx && errors.nrTrx &&
                                            <div className="text-danger">{t(errors.nrTrx, {min: 1})}</div>}
                                    </div>
                                    <div className="col-12 align-self-end">
                                        <button type="submit" className="btn gg-btn gg-btn-submit mb-2 me-2">
                                            {t('Search')}
                                        </button>
                                        <button type="button" className="btn btn-secondary text-white mb-2"
                                                onClick={() => {
                                                    const defaults = getDefaultFilters();
                                                    resetForm({values: defaults});
                                                    setActiveFilters(defaults);
                                                    handleSearch(defaults);
                                                }}>
                                            {t('Reset')}
                                        </button>
                                        {submitCount > 0 && (errors as any).dateRange &&
                                            <div className="text-danger mt-2">{t((errors as any).dateRange)}</div>}
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </FiltersCard>

            {loading && <Loader/>}

            <h5 className="mt-4">{t('NewUsersStatistics')}</h5>
            <div className="row mb-3 gg-kpis">
                <KpiTile label={t('TotalUsers')} value={data.userCounts?.Total ?? '—'} variant="primary"/>
                <KpiTile label={t('NewUsersRatio')} value={data.newUsersRatio ?? '—'} variant="info"/>
            </div>
            {data.userCounts && data.userCounts.Counts.length > 0 && (
                <div className="table-responsive border p-1 rounded-2 mb-3">
                    <table className="table table-sm table-striped-columns small table-logs">
                        <thead>
                        <tr>
                            <th>{t('UserCountByProfileType')}</th>
                            <th>{t('TotalUsers')}</th>
                        </tr>
                        </thead>
                        <tbody className="table-group-divider">
                        {data.userCounts.Counts.map((row, idx) => (
                            <tr key={row.Type + '_' + idx}>
                                <td>{row.Type || '—'}</td>
                                <td>{row.Count}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            <h5 className="mt-4">{t('TransactionConsumptionStatistics')}</h5>
            <p className="text-muted small mb-2">{t('TransactionConsumptionStatisticsHint')}</p>
            <div className="row mb-3 gg-kpis">
                <KpiTile label={t('AverageTrxTime')}
                         value={data.avgTrxTime && isTimeSpanFormat(data.avgTrxTime) ? roundTimeSpanToMillis(data.avgTrxTime) : t('NoData')}
                         note={averageTrxTimeCap ? t('AverageTrxTimeNote', {count: averageTrxTimeCap}) : t('AverageTrxTimeNoteUnlimited')}
                         variant="secondary"/>
                <KpiTile label={t('P2GTransactions')} value={data.p2gAndG2g?.P2G ?? '—'} variant="success"/>
                <KpiTile label={t('G2GTransactions')} value={data.p2gAndG2g?.G2G ?? '—'} variant="warning"/>
                <KpiTile label={t('TotalTransactions')} value={data.p2gAndG2g?.Total ?? '—'} variant="info"/>
            </div>

            {activeFilters.targetProfileId && (
                data.profileNotFound ? (
                    <div className="text-muted mb-3">
                        {t('StatisticsProfileNotFound', {profileId: String(activeFilters.targetProfileId)})}
                    </div>
                ) : (
                    <div className="row mb-3 gg-kpis">
                        <KpiTile label={t('NrOfTrxForUser')} value={data.nrOfTrxForUser ?? '—'} variant="primary"/>
                        <KpiTile label={t('NrOfLoginsForUser')} value={data.nrOfLoginsForUser ?? '—'} variant="secondary"/>
                    </div>
                )
            )}
        </div>
    );
};

const KpiTile: React.FC<{
    label: string;
    value: React.ReactNode;
    variant: 'info' | 'primary' | 'success' | 'warning' | 'danger' | 'secondary';
    note?: string;
}> = ({label, value, variant, note}) => (
    <div className="col-md-3 col-sm-6 mb-2">
        <div className={`card gg-kpi-card gg-kpi-${variant} shadow-sm border-0 h-100`}>
            <div className="card-body py-3">
                <div className="text-uppercase small fw-semibold gg-kpi-label mb-1">{label}</div>
                <div className="gg-kpi-value">
                    <span className="display-6 fw-bold">{value}</span>
                </div>
                {note && <div className="text-muted small mt-1">{note}</div>}
            </div>
        </div>
    </div>
);

export default Statistics;
